import { useEffect, useState } from 'react';
import { useCall } from 'contexts/CallContext';
import { useLeadDetail } from 'contexts/LeadDetailContext';
import signalRNotificationService from 'services/signalrNotificationService';
import { toast } from 'sonner';
import { ConfirmModal } from 'components/shared/ConfirmModal';
import { PhoneIcon } from '@heroicons/react/24/outline';

const GlobalSignalRListener = () => {
    const {
        setLeadData,
        setOzonetelUcid,
        setActiveProvider,
        setModalOpen, // From CallContext
        isModalOpen, // To check if we need to open it
        setCallStatus 
    } = useCall();

    const { openLeadDetail, isOpen } = useLeadDetail();
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingLead, setPendingLead] = useState(null);

    // Listen for Ozonetel Inbound Popup Signal
    useEffect(() => {
        const handleInboundPopup = (data) => {
            // console.log('📞 Inbound Popup Triggered:', data);

            if (data && data.leadId) {

                // Check for Disconnected status
                const normalizedStatus = (data.status || '').toLowerCase();
                const isDisconnected = ['disconnected', 'disconnect', 'completed', 'failed', 'busy', 'notanswered'].includes(normalizedStatus);
                
                if (isDisconnected) {
                    // Close local confirmation if open
                    setPendingLead(null);
                    setShowConfirmModal(false);

                    // Update call state to ended
                    setCallStatus('ended');
                    setOzonetelUcid(null); // Stop polling immediately

                    toast.info(`Call Disconnected`);
                    return; // Stop further processing
                }

                // Construct basic lead object
                // Construct lead object using enriched details if available
                const details = data.leadDetails || {};
                
                // 1. Resolve Name and Number robustly
                const incomingNumber = (data.callerNumber || details.PhoneNumber || data.phoneNumber || 'Unknown').toString().replace(/undefined/g, '').trim() || 'Unknown';
                let fullName = (details.FullName || (data.FirstName && data.LastName ? `${data.FirstName} ${data.LastName}` : '')).toString().replace(/undefined/g, '').trim();
                
                // Fallback to number if name is missing or "Unknown Lead"
                if (!fullName || fullName.toLowerCase() === 'unknown lead') {
                    fullName = incomingNumber;
                }

                // Split FullName for components that expect first/last
                const nameParts = fullName.split(' ');
                const firstName = nameParts[0] || 'Unknown';
                const lastName = nameParts.slice(1).join(' ') || (fullName === incomingNumber ? '' : 'Lead');

                const lead = {
                    ...data,
                    ...details,
                    id: data.leadId,
                    leadId: data.leadId,
                    firstName: firstName,
                    lastName: lastName,
                    fullName: fullName,
                    name: fullName,
                    email: details.Email || data.Email || '',
                    phoneNumber: incomingNumber,
                    phone: incomingNumber
                };

                // Check for Connected status
                const isConnected = ['connected', 'answered', 'success'].includes(normalizedStatus);
                if (isConnected) {
                    setCallStatus('connected');
                    setLeadData(lead);
                    setActiveProvider('OZONETEL');
                    if (data.ucid) setOzonetelUcid(data.ucid);
                }

                // Treat all non-disconnected events as incoming calls requiring confirmation/notification
                if (isOpen) {
                    // If Lead Details are ALREADY open:
                    // 1. Update context immediately so the call widget reflects reality without interruption
                    setLeadData(lead);
                    if (data.ucid) setOzonetelUcid(data.ucid);
                    setActiveProvider('OZONETEL');

                    // 2. Show toast info
                    toast.info(`Incoming call from ${lead.fullName}`);

                    // 3. Update the lead detail view locally
                    openLeadDetail(data.leadId);
                } else {
                    // If closed, ask for confirmation BEFORE activating global call state
                    // Stash the full `data` and `lead` to apply later

                    // Skip confirmation for OUTBOUND calls (initiated by agent)
                    if (data.callType != 'InBound') {
                        return;
                    }

                    setPendingLead({ lead, rawData: data });
                    setShowConfirmModal(true);
                }
            }
        };

        const subId = signalRNotificationService.subscribe('ReceiveInboundCallPopup', handleInboundPopup);
        return () => signalRNotificationService.unsubscribe(subId);
    }, [setLeadData, setOzonetelUcid, setActiveProvider, setModalOpen, isModalOpen, openLeadDetail, isOpen]);

    const handleConfirmOpen = () => {
        if (pendingLead) {
            const { lead, rawData } = pendingLead;

            // 1. Now apply the global call state
            setLeadData(lead);
            if (rawData.ucid) setOzonetelUcid(rawData.ucid);
            setActiveProvider('OZONETEL');

            // 2. Open the UI
            openLeadDetail(lead.id);
            setPendingLead(null);
        }
        setShowConfirmModal(false);
    };

    return (
        <ConfirmModal
            show={showConfirmModal}
            onClose={() => setShowConfirmModal(false)}
            onOk={handleConfirmOpen}
            state="pending"
            messages={{
                pending: {
                    title: "Incoming Ozonetel Call",
                    description: pendingLead
                        ? <div>
                            <p className="mb-2">Incoming call from <strong>{pendingLead.lead.fullName}</strong>.</p>
                            <p className="border-primary-200 text-sm text-gray-500 bg-gray-50 p-2 rounded border border-dashed">
                                💡 To receive this call open the <strong>Ozonetel Dialer</strong>.
                            </p>
                            <p className="mt-2 text-sm">Do you want to open the lead details?</p>
                          </div>
                        : "Incoming call detected. Do you want to open lead details?",
                    actionText: "Yes, Open",
                    Icon: PhoneIcon,
                    iconClassName: "text-primary-600 animate-pulse"
                }
            }}
        />
    );
};

export default GlobalSignalRListener;
